import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { acquisition as acquisitionApi, prospects as prospectsApi } from "@/api";
import ActaConfirmDialog from "@/components/ActaConfirmDialog";
import ActaStandardPage from "@/components/layout/ActaStandardPage";

const STATUS_LABELS = {
  new: "Nouveau",
  to_assign: "À affecter",
  assigned: "Affecté",
};

function Section({ title, children }) {
  return (
    <section className="lirah-card p-5">
      <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 border-b border-[var(--do-border)] py-2 text-sm last:border-0">
      <span className="text-[var(--do-muted)]">{label}</span>
      <span className="break-words font-medium text-[var(--do-ink)]">{value || "—"}</span>
    </div>
  );
}

export default function AcquisitionLeadDetail() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [assignedUserId, setAssignedUserId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setError("");
    try {
      const [leadData, users] = await Promise.all([
        acquisitionApi.getLead(leadId),
        prospectsApi.listCommercialUsers(),
      ]);
      setLead(leadData);
      setCommercials(Array.isArray(users) ? users : []);
      setAssignedUserId(leadData?.assignedUserId || leadData?.duplicate?.contact?.assignedUserId || "");
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de charger le lead.");
    }
  }

  useEffect(() => { load(); }, [leadId]);

  const duplicate = lead?.duplicate;
  const unresolvedDuplicate = duplicate && !duplicate?.resolution && !lead?.rejected;
  const canAssign = lead?.status === "to_assign" && !lead?.rejected;
  const existingOwnerId = unresolvedDuplicate
    ? ""
    : lead?.duplicate?.resolution?.action === "associate"
      ? lead?.duplicate?.contact?.assignedUserId || ""
      : "";
  const effectiveCommercialId = existingOwnerId || assignedUserId;

  const selectedCommercial = useMemo(
    () => commercials.find((item) => item.id === effectiveCommercialId),
    [commercials, assignedUserId]
  );

  async function confirmAssignment() {
    setSaving(true);
    setError("");
    try {
      const updated = await acquisitionApi.assignLead(leadId, effectiveCommercialId);
      setLead(updated);
      setConfirmOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d'affecter le lead.");
    } finally {
      setSaving(false);
    }
  }

  async function resolveDuplicate(action) {
    setSaving(true);
    setError("");
    try {
      const updated = await acquisitionApi.resolveDuplicate(leadId, {
        action,
        prospectId: duplicate?.prospectId,
      });
      setLead(updated);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de résoudre le doublon.");
    } finally {
      setSaving(false);
    }
  }

  async function anonymizeLead() {
    setSaving(true);
    setError("");
    try {
      const updated = await acquisitionApi.anonymizeLead(leadId, {
        reason: "manual_erasure",
      });
      setLead(updated);
      setPrivacyOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "object"
          ? detail?.message || "Coordination Commerce requise."
          : detail || "Impossible d'anonymiser le lead."
      );
      setPrivacyOpen(false);
    } finally {
      setSaving(false);
    }
  }

  async function confirmReject() {
    setSaving(true);
    setError("");
    try {
      const updated = await acquisitionApi.rejectLead(leadId, rejectReason);
      setLead(updated);
      setRejectOpen(false);
      setRejectReason("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de rejeter le lead.");
    } finally {
      setSaving(false);
    }
  }

  if (!lead) {
    return (
      <ActaStandardPage>
        <p className="text-sm text-[var(--do-muted)]">{error || "Chargement…"}</p>
      </ActaStandardPage>
    );
  }

  const provenance = lead.provenance || {};
  const source = provenance.source || {};

  return (
    <ActaStandardPage testId="acquisition-lead-detail">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Link to="/app/acquisition/leads" className="text-sm font-bold text-[var(--do-accent)]">← Retour aux leads</Link>
          <p className="label-eyebrow mt-4">Lead Acquisition</p>
          <h1 className="font-heading text-3xl font-black text-[var(--do-ink)]">
            {[lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.company || "Sans nom"}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="acta-status-badge">{STATUS_LABELS[lead.status] || lead.statusLabel}</span>
            {lead.rejected ? <span className="acta-status-badge">Rejeté</span> : null}
            {(lead.alerts || []).map((alert) => <span key={alert.code} className="acta-status-badge">{alert.label}</span>)}
          </div>
        </div>

        {canAssign ? (
          <div className="w-full max-w-sm rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
            <label className="text-xs font-bold uppercase tracking-wide text-[var(--do-muted)]">Commercial</label>
            <select value={effectiveCommercialId} disabled={Boolean(existingOwnerId)} onChange={(e) => setAssignedUserId(e.target.value)} className="mt-2 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm">
              <option value="">Sélectionner</option>
              {commercials.map((user) => <option key={user.id} value={user.id}>{user.name || user.email}</option>)}
            </select>
            <button type="button" className="btn-primary mt-3 w-full py-3" disabled={!effectiveCommercialId} onClick={() => setConfirmOpen(true)}>
              Associer à un commercial
            </button>
          </div>
        ) : null}
      </div>

      {error ? <div className="mt-4 rounded-2xl border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-4 text-sm text-[var(--do-danger)]">{error}</div> : null}

      {unresolvedDuplicate ? (
        <section className="mt-5 rounded-2xl border border-[var(--do-border-strong)] bg-[var(--do-accent-soft)] p-5">
          <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">Doublon potentiel</h2>
          <p className="mt-2 text-sm text-[var(--do-muted)]">
            Un Contact Commerce correspond à ce lead ({duplicate.reason}). Choisissez explicitement le traitement.
          </p>
          {duplicate?.contact?.assignedUserName ? (
            <p className="mt-2 text-sm font-bold text-[var(--do-ink)]">
              Commercial actuel : {duplicate.contact.assignedUserName}
            </p>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className="btn-primary px-4 py-2" disabled={saving} onClick={() => resolveDuplicate("associate")}>Associer au contact existant</button>
            <button type="button" className="btn-secondary px-4 py-2" disabled={saving} onClick={() => resolveDuplicate("create_distinct")}>Créer un contact distinct</button>
            <button type="button" className="btn-secondary px-4 py-2" disabled={saving} onClick={() => setRejectOpen(true)}>Rejeter</button>
          </div>
        </section>
      ) : null}

      <div className="mt-6 grid gap-4 xl:grid-cols-2">
        <Section title="Identité">
          <Row label="Prénom" value={lead.firstName} />
          <Row label="Nom" value={lead.lastName} />
          <Row label="Société" value={lead.company} />
          <Row label="E-mail" value={lead.email} />
          <Row label="Téléphone" value={lead.phone} />
        </Section>

        <Section title="Provenance">
          <Row label="Source" value={source.name} />
          <Row label="Provider" value={provenance.provider} />
          <Row label="Canal" value={provenance.channel} />
          <Row label="Campagne" value={provenance.campaignName} />
          <Row label="Formulaire" value={provenance.formName || provenance.formId} />
          <Row label="Reçu le" value={provenance.receivedAt ? new Date(provenance.receivedAt).toLocaleString("fr-FR") : ""} />
        </Section>

        <Section title="Besoin">
          <p className="whitespace-pre-wrap text-sm leading-6 text-[var(--do-ink)]">{lead.message || "Aucun message."}</p>
        </Section>

        <Section title="Conformité">
          <Row label="Consentement" value={lead?.consent?.status} />
          <Row label="Finalité" value={lead?.consent?.purpose} />
          <Row label="Base" value={lead?.consent?.legalBasis} />
          <Row label="Opposition" value={lead?.opposition?.status} />
        </Section>

        <Section title="Affectation">
          <Row label="Commercial" value={lead.assignedUserName} />
          <Row label="Date" value={lead.assignedAt ? new Date(lead.assignedAt).toLocaleString("fr-FR") : ""} />
          <Row label="Contact Commerce" value={lead.prospectId} />
          {lead.prospectId ? (
            <Link className="mt-3 inline-flex font-bold text-[var(--do-accent)]" to={`/app/commerce/prospects/${lead.prospectId}`}>
              Ouvrir le contact →
            </Link>
          ) : null}
        </Section>

        <Section title="Historique">
          <div className="space-y-3">
            {(lead.history || []).slice().reverse().map((event) => (
              <div key={event.id} className="border-b border-[var(--do-border)] pb-3 last:border-0">
                <div className="text-sm font-bold text-[var(--do-ink)]">{event.type}</div>
                <div className="mt-1 text-xs text-[var(--do-muted)]">{event.createdAt ? new Date(event.createdAt).toLocaleString("fr-FR") : ""}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {lead.status !== "assigned" && !lead.rejected ? (
          <button type="button" className="btn-secondary px-4 py-3" onClick={() => setRejectOpen(true)}>Rejeter le lead</button>
        ) : null}
        {!lead.anonymized ? (
          <button type="button" className="btn-secondary px-4 py-3" onClick={() => setPrivacyOpen(true)}>Anonymiser</button>
        ) : (
          <span className="acta-status-badge">Anonymisé</span>
        )}
      </div>

      <ActaConfirmDialog
        open={privacyOpen}
        eyebrow="RGPD"
        title="Anonymiser ce lead"
        description={lead.prospectId ? "Si le Contact Commerce est encore actif, l’opération sera bloquée jusqu’à son traitement dans Commerce." : "Les données personnelles du lead seront supprimées de manière irréversible."}
        confirmLabel="Anonymiser"
        saving={saving}
        danger
        onClose={() => setPrivacyOpen(false)}
        onConfirm={anonymizeLead}
      />

      <ActaConfirmDialog
        open={confirmOpen}
        title="Confirmer l’affectation"
        description={`Ce lead sera affecté à ${selectedCommercial?.name || selectedCommercial?.email || "ce commercial"} et deviendra visible dans Commerce comme « Nouveau lead ».`}
        confirmLabel="Confirmer l’affectation"
        saving={saving}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmAssignment}
      />

      {rejectOpen ? (
        <div className="fixed inset-0 z-[170] flex items-center justify-center acta-modal-overlay p-4" onClick={() => !saving && setRejectOpen(false)}>
          <div className="acta-modal-surface w-full max-w-lg rounded-3xl bg-[var(--do-bg-solid)] p-6" onClick={(event) => event.stopPropagation()}>
            <h2 className="font-heading text-xl font-black">Rejeter le lead</h2>
            <p className="mt-2 text-sm text-[var(--do-muted)]">Le motif est obligatoire et sera historisé.</p>
            <textarea className="mt-4 min-h-28 w-full rounded-xl border border-[var(--do-border)] bg-transparent p-3" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Motif du rejet" />
            <div className="mt-5 flex justify-end gap-3">
              <button className="btn-secondary px-4 py-2" onClick={() => setRejectOpen(false)} disabled={saving}>Annuler</button>
              <button className="btn-primary px-4 py-2" onClick={confirmReject} disabled={saving || !rejectReason.trim()}>{saving ? "Enregistrement…" : "Confirmer le rejet"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </ActaStandardPage>
  );
}
